import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, Users as UsersIcon, Edit, Loader2, MoreVertical, Mail, KeyRound, UserCheck, UserX, Trash2, ShieldAlert, Power } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Card } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import SalviaLayout from "@/components/SalviaLayout";
import { useLocation } from "wouter";
import { usePageTitle } from "@/hooks/usePageTitle";
import {
  CHANNEL_SCOPE_OPTIONS,
  STRUCTURAL_ROLE_OPTIONS,
  deriveAccessModelFromLegacyRole,
  getDefaultOperationalProfile,
  getLegacyRoleLabel,
  getOperationalProfileOptions,
  resolveLegacyRoleFromAccessModel,
  type ChannelScopeMode,
  type LegacyUserRole,
  type OperationalProfile,
  type StructuralRole,
} from "@/lib/userStructuralRole";

interface FormData {
  name: string;
  email: string;
  phone: string;
  role: string;
  channelId: string;
  companyId: string;
}

interface EditingUser extends FormData {
  id: number;
}

// Dados originais do user na lista (para status real)
interface OriginalUserData {
  id: number;
  isActive: boolean;
  deletedAt: any;
  deactivatedAt: any;
}

interface OriginalEditableSnapshot {
  name: string;
  phone: string;
  role: LegacyUserRole;
  channelId: string;
  companyId: string;
  channelIds: number[];
}

type LifecycleAction = "deactivate" | "activate" | "delete";

interface ConfirmLifecycleDialog {
  open: boolean;
  action: LifecycleAction | null;
  userId: number | null;
  userName: string;
  userEmail: string;
  reason: string;
}

export default function Users() {
  usePageTitle("Usuários");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive" | "deleted">("active");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editingUser, setEditingUser] = useState<EditingUser | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [confirmResetDialog, setConfirmResetDialog] = useState<{ open: boolean; userId: number | null; userEmail: string }>({ open: false, userId: null, userEmail: "" });
  const [creationContextPrefill, setCreationContextPrefill] = useState<{ channelId: string; companyId: string } | null>(null);
  const [confirmLifecycle, setConfirmLifecycle] = useState<ConfirmLifecycleDialog>({
    open: false,
    action: null,
    userId: null,
    userName: "",
    userEmail: "",
    reason: "",
  });
  // Dados originais do user sendo editado (para toggle de status)
  const [originalUserData, setOriginalUserData] = useState<OriginalUserData | null>(null);
  const [originalEditableSnapshot, setOriginalEditableSnapshot] = useState<OriginalEditableSnapshot | null>(null);
  const [keepDialogOpenAfterUserSave, setKeepDialogOpenAfterUserSave] = useState(false);
  // Modal de confirmação inline (toggle dentro do modal de edição)
  const [inlineStatusConfirm, setInlineStatusConfirm] = useState<{
    open: boolean;
    action: "activate" | "deactivate" | null;
    reason: string;
  }>({ open: false, action: null, reason: "" });

  const { user } = useAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const companyId = params.get("companyId")?.trim() ?? "";
    const channelId = params.get("channelId")?.trim() ?? "";

    if (!companyId && !channelId) return;

    setCreationContextPrefill({ channelId, companyId });
  }, []);

  useEffect(() => {
    if (!creationContextPrefill || isDialogOpen || editingUser) return;
    if (user?.role !== "admin") return;

    setIsCreating(true);
    syncAccessModelFromLegacyRole("client_rh");
    setEditingUser({
      id: 0,
      name: "",
      email: "",
      phone: "",
      role: "client_rh",
      channelId: creationContextPrefill.channelId,
      companyId: creationContextPrefill.companyId,
    });
    setOriginalUserData(null);
    setSelectedChannelIds([]);
    setSelectedUserServiceIds([]);
    setSelectedFunctionalProfiles({});
    setFunctionalAssignmentNotesByService({});
    setValidationErrors([]);
    setInlineStatusConfirm({ open: false, action: null, reason: "" });
    setIsDialogOpen(true);

    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.delete("companyId");
      url.searchParams.delete("channelId");
      const nextUrl = `${url.pathname}${url.search}${url.hash}`;
      window.history.replaceState({}, "", nextUrl);
    }

    setCreationContextPrefill(null);
  }, [creationContextPrefill, editingUser, isDialogOpen]);

  // tRPC queries
  const [selectedChannelIds, setSelectedChannelIds] = useState<number[]>([]);
  const [selectedUserServiceIds, setSelectedUserServiceIds] = useState<number[]>([]);
  const [selectedFunctionalProfiles, setSelectedFunctionalProfiles] = useState<Record<string, string>>({});
  const [functionalAssignmentNotesByService, setFunctionalAssignmentNotesByService] = useState<Record<string, string>>({});
  const [structuralRole, setStructuralRole] = useState<StructuralRole>("client");
  const [channelScopeMode, setChannelScopeMode] = useState<ChannelScopeMode>("single");
  const [operationalProfile, setOperationalProfile] = useState<OperationalProfile>("client_rh");
  const [resolvedCreationPreviewContextKey, setResolvedCreationPreviewContextKey] = useState<string | null>(null);
  const [resolvedEditPreviewContextKey, setResolvedEditPreviewContextKey] = useState<string | null>(null);

  const syncAccessModelFromLegacyRole = (legacyRole: LegacyUserRole) => {
    const model = deriveAccessModelFromLegacyRole(legacyRole);
    setStructuralRole(model.structuralRole);
    setChannelScopeMode(model.channelScopeMode);
    setOperationalProfile(model.operationalProfile);
  };

  const resetOperationalAccessSelections = () => {
    setSelectedUserServiceIds([]);
    setSelectedFunctionalProfiles({});
    setFunctionalAssignmentNotesByService({});
  };

  const isAdminStructuralRole = structuralRole === "admin";
  const isChannelStructuralRole = structuralRole === "channel";
  const isClientStructuralRole = structuralRole === "client";
  const isMultiChannelScope = isChannelStructuralRole && channelScopeMode === "multi";
  const isSingleChannelScope = isChannelStructuralRole && channelScopeMode === "single";
  const operationalProfileOptions = ((isMultiChannelScope
    ? getOperationalProfileOptions(structuralRole).filter((option) => option.value === "channel_manager")
    : getOperationalProfileOptions(structuralRole)).filter((option) => {
      if (structuralRole !== "admin") {
        return true;
      }

      if (user?.role === "salvia_operation_admin") {
        return option.value !== "platform_master";
      }

      return true;
    })) as ReturnType<typeof getOperationalProfileOptions>;
  const resolvedFormLegacyRole = resolveLegacyRoleFromAccessModel({
    structuralRole,
    channelScopeMode,
    operationalProfile,
  });
  const currentDraftChannelIds = isMultiChannelScope ? selectedChannelIds : [];
  const currentDraftPrimaryChannelId = isMultiChannelScope
    ? (selectedChannelIds[0] ? String(selectedChannelIds[0]) : "")
    : (editingUser?.channelId ?? "");
  const currentDraftCompanyId = isClientStructuralRole ? (editingUser?.companyId ?? "") : "";
  const buildUserDraftPayload = () => {
    if (!editingUser) return null;

    const payload: any = {
      name: editingUser.name.trim(),
      phone: editingUser.phone.trim() || null,
      role: resolvedFormLegacyRole as any,
      channelId: isMultiChannelScope ? (selectedChannelIds[0] || null) : (editingUser.channelId ? parseInt(editingUser.channelId) : null),
      companyId: editingUser.companyId ? parseInt(editingUser.companyId) : null,
    };

    if (isMultiChannelScope) {
      payload.channelIds = selectedChannelIds;
    }

    return payload;
  };
  const normalizedDraftChannelIdsKey = JSON.stringify([...currentDraftChannelIds].sort((a, b) => a - b));
  const normalizedOriginalChannelIdsKey = JSON.stringify([...(originalEditableSnapshot?.channelIds ?? [])].sort((a, b) => a - b));
  const hasPendingUserDraftChanges = !!editingUser && !!originalEditableSnapshot && (
    editingUser.name.trim() !== originalEditableSnapshot.name
    || (editingUser.phone.trim() || "") !== originalEditableSnapshot.phone
    || resolvedFormLegacyRole !== originalEditableSnapshot.role
    || currentDraftPrimaryChannelId !== originalEditableSnapshot.channelId
    || currentDraftCompanyId !== originalEditableSnapshot.companyId
    || normalizedDraftChannelIdsKey !== normalizedOriginalChannelIdsKey
  );
  const isCreationPreviewReady = !!editingUser && (
    isAdminStructuralRole
      || (isMultiChannelScope && selectedChannelIds.length > 0)
      || (isSingleChannelScope && !!editingUser.channelId)
      || (isClientStructuralRole && !!editingUser.channelId && !!editingUser.companyId)
  );

  const handleStructuralRoleChange = (value: StructuralRole) => {
    const nextOperationalProfile = value === "admin" && user?.role === "salvia_operation_admin"
      ? "platform_operations_manager"
      : getDefaultOperationalProfile(value);
    setStructuralRole(value);
    setChannelScopeMode("single");
    setOperationalProfile(nextOperationalProfile);
    setSelectedChannelIds([]);
    resetOperationalAccessSelections();

    if (!editingUser) return;

    setEditingUser({
      ...editingUser,
      channelId: value === "admin" ? "" : editingUser.channelId,
      companyId: value === "client" ? editingUser.companyId : "",
      role: resolveLegacyRoleFromAccessModel({
        structuralRole: value,
        channelScopeMode: "single",
        operationalProfile: nextOperationalProfile,
      }),
    });
  };

  const handleChannelScopeModeChange = (value: ChannelScopeMode) => {
    const nextOperationalProfile = value === "multi" ? "channel_manager" : operationalProfile;
    setChannelScopeMode(value);
    setOperationalProfile(nextOperationalProfile);
    setSelectedChannelIds([]);
    resetOperationalAccessSelections();

    if (!editingUser) return;

    setEditingUser({
      ...editingUser,
      channelId: "",
      companyId: "",
      role: resolveLegacyRoleFromAccessModel({
        structuralRole,
        channelScopeMode: value,
        operationalProfile: nextOperationalProfile,
      }),
    });
  };

  const handleOperationalProfileChange = (value: OperationalProfile) => {
    setOperationalProfile(value);
    resetOperationalAccessSelections();

    if (!editingUser) return;

    setEditingUser({
      ...editingUser,
      companyId: structuralRole === "client" ? editingUser.companyId : "",
      role: resolveLegacyRoleFromAccessModel({
        structuralRole,
        channelScopeMode,
        operationalProfile: value,
      }),
    });
  };

  useEffect(() => {
    if (operationalProfileOptions.some((option) => option.value === operationalProfile)) return;
    const fallbackProfile = operationalProfileOptions[0]?.value
      ?? (structuralRole === "admin" && user?.role === "salvia_operation_admin"
        ? "platform_operations_manager"
        : getDefaultOperationalProfile(structuralRole));
    setOperationalProfile(fallbackProfile);
  }, [operationalProfile, operationalProfileOptions, structuralRole, user?.role]);

  const canManageUsersScope = user?.role === "admin" || user?.role === "salvia_operation_admin" || user?.role === "channel_admin" || user?.role === "channel_admin_multi" || user?.role === "channel_operator" || user?.role === "channel_consultant" || user?.role === "client_rh";
  const canCreateOrEditUsers = user?.role === "admin" || user?.role === "salvia_operation_admin";
  const canResetPasswords = user?.role === "admin" || user?.role === "salvia_operation_admin";
  const canDeleteUsersGlobally = user?.role === "admin";
  const managementContextLabel = user?.role === "client_rh"
    ? "Administração da Empresa"
    : user?.role === "admin" || user?.role === "salvia_operation_admin"
      ? "Núcleo da Plataforma"
      : "Administração do Canal";
  const managementDescription = user?.role === "client_rh"
    ? "Acompanhe os usuários da sua empresa. Neste escopo, você pode apenas enviar convite, inativar e reativar usuários já existentes."
    : user?.role === "admin"
      ? "Cadastre e administre os usuários estruturais do Portal PJC no contexto do Núcleo da Plataforma."
      : user?.role === "salvia_operation_admin"
        ? "Cadastre e administre usuários operacionais do Portal PJC no contexto do Núcleo da Plataforma, sem acesso para criar ou promover Admin Master Sálvia."
        : "Acompanhe os usuários do seu canal e dos clientes da sua carteira. Neste escopo, você pode apenas enviar convite, inativar e reativar usuários já existentes.";

  const { data: channels } = trpc.users.listAssignableChannels.useQuery(undefined, {
    enabled: canManageUsersScope,
  });

  const { data: companyDisplayOptions } = trpc.users.listAssignableCompanies.useQuery(undefined, {
    enabled: canManageUsersScope,
  });
  const { data: companies } = trpc.users.listAssignableCompanies.useQuery(
    editingUser?.channelId && editingUser.channelId !== "" ? { channelId: parseInt(editingUser.channelId) } : undefined,
    { enabled: canManageUsersScope && (!!editingUser?.channelId || user?.role === "client_rh") }
  );
  const assignableCompanies = Array.isArray(companies) ? companies : (companies as any)?.items ?? [];
  const companyDisplayList = Array.isArray(companyDisplayOptions) ? companyDisplayOptions : (companyDisplayOptions as any)?.items ?? [];
  const companyNameById = new Map<number, string>(companyDisplayList.map((company: any) => [company.id, company.nomeFantasia || company.name]));

  const { data: users, isLoading, refetch } = trpc.users.listAll.useQuery(undefined, {
    enabled: canManageUsersScope,
  });

  const activePermissionUserId = !isCreating && editingUser?.id ? editingUser.id : undefined;

  const creationPreviewInput = editingUser && isCreationPreviewReady
    ? {
        role: resolvedFormLegacyRole as any,
        channelId: isMultiChannelScope
          ? null
          : editingUser.channelId
            ? parseInt(editingUser.channelId)
            : null,
        channelIds: isMultiChannelScope ? selectedChannelIds : [],
        companyId: editingUser.companyId ? parseInt(editingUser.companyId) : null,
      }
    : undefined;
  const creationPreviewContextKey = creationPreviewInput
    ? JSON.stringify({
        role: creationPreviewInput.role,
        channelId: creationPreviewInput.channelId ?? null,
        channelIds: creationPreviewInput.channelIds,
        companyId: creationPreviewInput.companyId ?? null,
      })
    : null;
  const editPreviewInput = !isCreating && editingUser && isCreationPreviewReady
    ? {
        role: resolvedFormLegacyRole as any,
        channelId: isMultiChannelScope
          ? null
          : editingUser.channelId
            ? parseInt(editingUser.channelId)
            : null,
        channelIds: isMultiChannelScope ? selectedChannelIds : [],
        companyId: editingUser.companyId ? parseInt(editingUser.companyId) : null,
      }
    : undefined;
  const editPreviewContextKey = editPreviewInput
    ? JSON.stringify({
        role: editPreviewInput.role,
        channelId: editPreviewInput.channelId ?? null,
        channelIds: editPreviewInput.channelIds,
        companyId: editPreviewInput.companyId ?? null,
      })
    : null;

  const {
    data: userServicePermissions,
    isLoading: isLoadingUserServicePermissions,
    refetch: refetchUserServicePermissions,
  } = trpc.users.getServicePermissions.useQuery(
    activePermissionUserId ? { userId: activePermissionUserId } : undefined as any,
    {
      enabled: canManageUsersScope && !!activePermissionUserId && isDialogOpen,
    },
  );

  const {
    data: creationAccessPreview,
    error: creationAccessPreviewError,
    isLoading: isLoadingCreationAccessPreview,
    isFetching: isFetchingCreationAccessPreview,
  } = trpc.users.previewCreationAccess.useQuery(creationPreviewInput as any, {
    enabled: canManageUsersScope && isCreating && isDialogOpen && !!creationPreviewInput,
    retry: false,
  });

  const {
    data: editAccessPreview,
    error: editAccessPreviewError,
    isLoading: isLoadingEditAccessPreview,
    isFetching: isFetchingEditAccessPreview,
  } = trpc.users.previewCreationAccess.useQuery(editPreviewInput as any, {
    enabled: canManageUsersScope && !isCreating && isDialogOpen && !!editPreviewInput,
    retry: false,
  });

  useEffect(() => {
    if (!userServicePermissions || isCreating) return;
    setSelectedUserServiceIds(
      userServicePermissions.services
        .filter((service: any) => service.userHasPermission)
        .map((service: any) => service.id),
    );
  }, [userServicePermissions, isCreating]);

  useEffect(() => {
    if (!isCreating) return;
    setResolvedCreationPreviewContextKey(null);
  }, [creationPreviewContextKey, isCreating]);

  useEffect(() => {
    if (!isCreating) return;
    setResolvedEditPreviewContextKey(null);
  }, [isCreating]);

  useEffect(() => {
    if (isCreating || !editPreviewContextKey) {
      setResolvedEditPreviewContextKey(null);
      return;
    }
    if (!editAccessPreview || isFetchingEditAccessPreview) return;
    setResolvedEditPreviewContextKey(editPreviewContextKey);
  }, [editAccessPreview, editPreviewContextKey, isCreating, isFetchingEditAccessPreview]);

  useEffect(() => {
    if (!creationAccessPreview || !isCreating || !creationPreviewContextKey || isFetchingCreationAccessPreview) return;
    setResolvedCreationPreviewContextKey(creationPreviewContextKey);
  }, [creationAccessPreview, creationPreviewContextKey, isCreating, isFetchingCreationAccessPreview]);

  const effectiveCreationAccessPreview = isCreating && creationPreviewContextKey && resolvedCreationPreviewContextKey === creationPreviewContextKey
    ? creationAccessPreview
    : undefined;
  const effectiveEditAccessPreview = !isCreating && editPreviewContextKey && resolvedEditPreviewContextKey === editPreviewContextKey
    ? editAccessPreview
    : undefined;
  const isCreationPreviewResolving = isCreating
    && !!creationPreviewContextKey
    && (isLoadingCreationAccessPreview || isFetchingCreationAccessPreview || resolvedCreationPreviewContextKey !== creationPreviewContextKey);
  const isEditPreviewResolving = !isCreating
    && !!editPreviewContextKey
    && (isLoadingEditAccessPreview || isFetchingEditAccessPreview || resolvedEditPreviewContextKey !== editPreviewContextKey);

  useEffect(() => {
    if (!effectiveCreationAccessPreview || !isCreating) return;
    setSelectedUserServiceIds((current) => current.filter((id) => effectiveCreationAccessPreview.eligibleServices.some((service: any) => service.id === id)));
    setSelectedFunctionalProfiles((current) => {
      const next = { ...current };
      Object.keys(next).forEach((serviceKey) => {
        const group = effectiveCreationAccessPreview.functionalProfileOptions.find((item: any) => item.serviceKey === serviceKey);
        const isValid = group?.profiles?.some((profile: any) => profile.key === next[serviceKey]);
        if (!isValid) delete next[serviceKey];
      });
      return next;
    });
  }, [effectiveCreationAccessPreview, isCreating]);

  useEffect(() => {
    if (!effectiveEditAccessPreview || isCreating) return;
    setSelectedUserServiceIds((current) => current.filter((id) => effectiveEditAccessPreview.eligibleServices.some((service: any) => service.id === id)));
  }, [effectiveEditAccessPreview, isCreating]);

  const {
    data: userFunctionalAssignment,
    isLoading: isLoadingUserFunctionalAssignment,
    refetch: refetchUserFunctionalAssignment,
  } = trpc.accessProfiles.getUserAssignment.useQuery(
    activePermissionUserId ? { userId: activePermissionUserId, serviceKey: "nr1" } : undefined as any,
    {
      enabled: canManageUsersScope && !!activePermissionUserId && isDialogOpen,
    },
  );

  useEffect(() => {
    if (!userFunctionalAssignment || isCreating) return;
    setSelectedFunctionalProfiles({ nr1: userFunctionalAssignment.activeProfile?.profileKey ?? "" });
    setFunctionalAssignmentNotesByService({ nr1: "" });
  }, [userFunctionalAssignment, isCreating]);

  // Mutation única para atualizar usuário
  const updateUser = trpc.users.update.useMutation({
    onSuccess: () => {
      toast.success("Usuário atualizado com sucesso!");
      refetch();

      if (keepDialogOpenAfterUserSave) {
        setKeepDialogOpenAfterUserSave(false);
        setOriginalEditableSnapshot(editingUser ? {
          name: editingUser.name.trim(),
          phone: editingUser.phone.trim() || "",
          role: resolvedFormLegacyRole as LegacyUserRole,
          channelId: currentDraftPrimaryChannelId,
          companyId: currentDraftCompanyId,
          channelIds: [...currentDraftChannelIds],
        } : null);
        void refetchUserServicePermissions();
        void refetchUserFunctionalAssignment();
        return;
      }

      setIsDialogOpen(false);
      resetForm();
    },
    onError: (error) => {
      const errorMessage = error.message || "Erro ao atualizar usuário";
      setValidationErrors([errorMessage]);
      toast.error(errorMessage);
    },
  });

  // Mutation para criar usuário com cadastro completo
  const createCompleteUser = trpc.users.createComplete.useMutation({
    onSuccess: (result) => {
      toast.success(result.message || "Usuário cadastrado com sucesso.");
      setIsDialogOpen(false);
      resetForm();
      refetch();
    },
    onError: (error) => {
      const errorMessage = error.message || "Erro ao concluir cadastro completo do usuário";
      setValidationErrors([errorMessage]);
      toast.error(errorMessage);
    },
  });

  const saveUserServicePermissions = trpc.users.updateServicePermissions.useMutation({
    onSuccess: () => {
      toast.success("Permissões por serviço atualizadas com sucesso.");
      refetchUserServicePermissions();
      refetchUserFunctionalAssignment();
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao atualizar permissões por serviço");
    },
  });

  const assignFunctionalProfile = trpc.accessProfiles.assignUserProfile.useMutation({
    onSuccess: () => {
      toast.success("Perfil funcional do Nível 2 atualizado com sucesso.");
      refetchUserFunctionalAssignment();
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao atribuir perfil funcional");
    },
  });

  // Mutation para enviar convite
  const sendInvite = trpc.users.sendInvite.useMutation({
    onSuccess: () => {
      toast.success("Convite enviado com sucesso! O usuário receberá um email para definir a senha.");
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao enviar convite");
    },
  });

  // Mutation para resetar senha
  const resetPassword = trpc.users.resetPassword.useMutation({
    onSuccess: () => {
      toast.success("Email de redefinição de senha enviado com sucesso!");
      setConfirmResetDialog({ open: false, userId: null, userEmail: "" });
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao resetar senha");
      setConfirmResetDialog({ open: false, userId: null, userEmail: "" });
    },
  });

  // Mutation para inativar usuário
  const deactivateUser = trpc.users.deactivate.useMutation({
    onSuccess: () => {
      toast.success("Usuário inativado com sucesso.");
      closeLifecycleDialog();
      setInlineStatusConfirm({ open: false, action: null, reason: "" });
      // Atualizar originalUserData para refletir o novo status no modal
      if (originalUserData) setOriginalUserData({ ...originalUserData, isActive: false, deactivatedAt: new Date() });
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao inativar usuário");
      closeLifecycleDialog();
      setInlineStatusConfirm({ open: false, action: null, reason: "" });
    },
  });

  // Mutation para reativar usuário
  const activateUser = trpc.users.activate.useMutation({
    onSuccess: () => {
      toast.success("Usuário reativado com sucesso.");
      closeLifecycleDialog();
      setInlineStatusConfirm({ open: false, action: null, reason: "" });
      // Atualizar originalUserData para refletir o novo status no modal
      if (originalUserData) setOriginalUserData({ ...originalUserData, isActive: true, deactivatedAt: null });
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao reativar usuário");
      closeLifecycleDialog();
      setInlineStatusConfirm({ open: false, action: null, reason: "" });
    },
  });

  // Mutation para excluir usuário (soft delete)
  const deleteUser = trpc.users.delete.useMutation({
    onSuccess: () => {
      toast.success("Usuário excluído com sucesso.");
      closeLifecycleDialog();
      // Se o modal de edição está aberto, fechar e limpar
      if (isDialogOpen) {
        setIsDialogOpen(false);
        resetForm();
      }
      // Atualizar originalUserData para refletir exclusão
      if (originalUserData) {
        setOriginalUserData({ ...originalUserData, deletedAt: new Date(), isActive: false });
      }
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao excluir usuário");
      closeLifecycleDialog();
    },
  });

  const closeLifecycleDialog = () => {
    setConfirmLifecycle({ open: false, action: null, userId: null, userName: "", userEmail: "", reason: "" });
  };

  const openLifecycleDialog = (action: LifecycleAction, u: any) => {
    setConfirmLifecycle({
      open: true,
      action,
      userId: u.id,
      userName: u.name || u.email || "Usuário",
      userEmail: u.email || "",
      reason: "",
    });
  };

  const handleConfirmLifecycle = () => {
    if (!confirmLifecycle.userId || !confirmLifecycle.action) return;
    const payload = { userId: confirmLifecycle.userId, reason: confirmLifecycle.reason || undefined };
    if (confirmLifecycle.action === "deactivate") deactivateUser.mutate(payload);
    else if (confirmLifecycle.action === "activate") activateUser.mutate(payload);
    else if (confirmLifecycle.action === "delete") deleteUser.mutate(payload);
  };

  const isLifecyclePending = deactivateUser.isPending || activateUser.isPending || deleteUser.isPending;

  const resetForm = () => {
    setEditingUser(null);
    setValidationErrors([]);
    setSelectedChannelIds([]);
    setOriginalEditableSnapshot(null);
    setKeepDialogOpenAfterUserSave(false);
    resetOperationalAccessSelections();
    syncAccessModelFromLegacyRole("client_rh");
  };

  const handleOpenDialog = (u?: any) => {
    if (!canCreateOrEditUsers) {
      toast.error("Neste escopo, você pode apenas enviar convite, inativar e reativar usuários.");
      return;
    }

    if (u) {
      const nextRole = (u.role || "client_rh") as LegacyUserRole;
      setIsCreating(false);
      syncAccessModelFromLegacyRole(nextRole);
      setEditingUser({
        id: u.id,
        name: u.name || "",
        email: u.email || "",
        phone: u.phone || "",
        role: nextRole,
        channelId: u.channelId?.toString() || "",
        companyId: u.companyId?.toString() || "",
      });
      // V56-fix: Pre-load channelIds for multicanal users
      if (u.role === "channel_admin_multi" && u.channelIds) {
        setSelectedChannelIds(u.channelIds);
      } else {
        setSelectedChannelIds([]);
      }
      setOriginalEditableSnapshot({
        name: u.name || "",
        phone: u.phone || "",
        role: nextRole,
        channelId: u.channelId?.toString() || "",
        companyId: u.companyId?.toString() || "",
        channelIds: u.role === "channel_admin_multi" && u.channelIds ? [...u.channelIds] : [],
      });
      resetOperationalAccessSelections();
      // Guardar dados originais para o toggle de status
      setOriginalUserData({
        id: u.id,
        isActive: u.isActive,
        deletedAt: u.deletedAt,
        deactivatedAt: u.deactivatedAt,
      });
    } else {
      setIsCreating(true);
      syncAccessModelFromLegacyRole("client_rh");
      setEditingUser({
        id: 0,
        name: "",
        email: "",
        phone: "",
        role: "client_rh",
        channelId: creationContextPrefill?.channelId ?? "",
        companyId: creationContextPrefill?.companyId ?? "",
      });
      setOriginalUserData(null);
      setOriginalEditableSnapshot(null);
      setSelectedChannelIds([]);
      resetOperationalAccessSelections();
    }
    setValidationErrors([]);
    setInlineStatusConfirm({ open: false, action: null, reason: "" });
    setIsDialogOpen(true);
  };

  const validateForm = (): boolean => {
    const errors: string[] = [];
    if (!editingUser) return false;
    if (!editingUser.name.trim()) errors.push("Nome é obrigatório");
    if (isCreating && !editingUser.email.trim()) errors.push("Email é obrigatório");

    if (isAdminStructuralRole) {
      if (editingUser.channelId || editingUser.companyId) errors.push("Admin não deve ter canal ou empresa vinculados neste cadastro.");
    } else if (isMultiChannelScope) {
      if (selectedChannelIds.length === 0) errors.push("Selecione pelo menos um canal para a abrangência multicanal.");
    } else if (isSingleChannelScope) {
      if (!editingUser.channelId) errors.push("Canal é obrigatório para o papel estrutural Canal.");
      if (editingUser.companyId) errors.push("O papel estrutural Canal não deve ter empresa vinculada.");
    } else if (isClientStructuralRole) {
      if (!editingUser.channelId) errors.push("Canal é obrigatório para o papel estrutural Cliente.");
      if (!editingUser.companyId) errors.push("Empresa é obrigatória para o papel estrutural Cliente.");
    }

    if (isCreating && canManageUsersScope) {
      if (!isCreationPreviewReady) {
        errors.push("Defina o papel estrutural e os vínculos obrigatórios antes de concluir o cadastro.");
      } else if (isCreationPreviewResolving) {
        errors.push("Aguarde a resolução das elegibilidades antes de concluir o cadastro.");
      } else if (creationAccessPreviewError) {
        errors.push(creationAccessPreviewError.message || "Não foi possível resolver as elegibilidades deste cadastro inicial.");
      } else if (!effectiveCreationAccessPreview) {
        errors.push("Não foi possível resolver as elegibilidades deste cadastro inicial.");
      } else {
        if (effectiveCreationAccessPreview.eligibleServices.length === 0) {
          errors.push("Este cadastro ainda não possui nenhum serviço elegível para liberação operacional.");
        }
        if (selectedUserServiceIds.length === 0) {
          errors.push("Selecione pelo menos um serviço operacional para concluir o cadastro.");
        }
        if (functionalProfileCatalogNr1.length > 0 && !selectedFunctionalProfiles.nr1) {
          errors.push("Selecione o perfil funcional do Nível 2 para o NR1 antes de concluir o cadastro.");
        }
      }
    }
    if (errors.length > 0) {
      setValidationErrors(errors);
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm() || !editingUser) return;
    const payload = buildUserDraftPayload();
    if (!payload) return;

    if (isCreating) {
      const functionalAssignments = Object.entries(selectedFunctionalProfiles)
        .filter(([, profileKey]) => !!profileKey)
        .map(([serviceKey, profileKey]) => ({
          serviceKey,
          profileKey,
          notes: functionalAssignmentNotesByService[serviceKey] || null,
        }));

      createCompleteUser.mutate({
        email: editingUser.email.trim(),
        ...payload,
        serviceIds: selectedUserServiceIds,
        functionalAssignments,
        sendInvite: true,
      });
      return;
    }

    updateUser.mutate({ userId: editingUser.id, data: payload });
  };

  const handleToggleUserServicePermission = (serviceId: number, checked: boolean) => {
    if (checked) {
      setSelectedUserServiceIds((current) => [...current, serviceId]);
      return;
    }
    setSelectedUserServiceIds((current) => current.filter((id) => id !== serviceId));
  };
  const handleSaveUserServicePermissions = async () => {
    if (!editingUser || !editingUser.id) return;
    if (!validateForm()) return;

    try {
      if (hasPendingUserDraftChanges) {
        const payload = buildUserDraftPayload();
        if (!payload) return;
        setKeepDialogOpenAfterUserSave(true);
        await updateUser.mutateAsync({ userId: editingUser.id, data: payload });
      }

      await saveUserServicePermissions.mutateAsync({
        userId: editingUser.id,
        serviceIds: selectedUserServiceIds,
      });
    } catch {
      setKeepDialogOpenAfterUserSave(false);
    }
  };

  const handleSaveFunctionalProfile = async () => {
    const selectedFunctionalProfileKey = selectedFunctionalProfiles.nr1;
    if (!editingUser || !editingUser.id || !selectedFunctionalProfileKey) return;
    if (!validateForm()) return;

    try {
      if (hasPendingUserDraftChanges) {
        const payload = buildUserDraftPayload();
        if (!payload) return;
        setKeepDialogOpenAfterUserSave(true);
        await updateUser.mutateAsync({ userId: editingUser.id, data: payload });
      }

      await assignFunctionalProfile.mutateAsync({
        userId: editingUser.id,
        serviceKey: "nr1",
        profileKey: selectedFunctionalProfileKey,
        notes: functionalAssignmentNotesByService.nr1 || null,
      });
    } catch {
      setKeepDialogOpenAfterUserSave(false);
    }
  };

  const persistedPermissionsByServiceId = new Map((userServicePermissions?.services ?? []).map((service: any) => [service.id, service]));
  const servicePermissionCatalog = isCreating
    ? (effectiveCreationAccessPreview?.availableServices ?? effectiveCreationAccessPreview?.eligibleServices ?? []).map((service: any) => ({
        ...service,
        userHasPermission: selectedUserServiceIds.includes(service.id),
        permissionStatus: selectedUserServiceIds.includes(service.id) ? "active" : "unassigned",
      }))
    : (effectiveEditAccessPreview?.eligibleServices ?? []).map((service: any) => {
        const persisted = persistedPermissionsByServiceId.get(service.id);
        return {
          ...service,
          userHasPermission: selectedUserServiceIds.includes(service.id),
          permissionStatus: persisted?.permissionStatus ?? (selectedUserServiceIds.includes(service.id) ? "active" : "unassigned"),
          grantedAt: persisted?.grantedAt ?? null,
          revokedAt: persisted?.revokedAt ?? null,
        };
      });
  const hasChannelEnabledServicesWithoutEligibility = isCreating
    && servicePermissionCatalog.length > 0
    && !servicePermissionCatalog.some((service: any) => service.eligibleForUser)
    && servicePermissionCatalog.some((service: any) => service.channelEnabled);
  const functionalProfileCatalogNr1 = isCreating
    ? effectiveCreationAccessPreview?.functionalProfileOptions?.find((group: any) => group.serviceKey === "nr1")?.profiles ?? []
    : userFunctionalAssignment?.availableProfiles ?? [];

  // Filtrar por status e busca
  const filteredUsers = (users || []).filter(u => {
    // Filtro de status
    const isDeleted = !!u.deletedAt;
    const isActive = u.isActive && !isDeleted;
    const isInactive = !u.isActive && !isDeleted;

    if (statusFilter === "active" && !isActive) return false;
    if (statusFilter === "inactive" && !isInactive) return false;
    if (statusFilter === "deleted" && !isDeleted) return false;

    // Filtro de busca
    if (searchTerm) {
      return (
        u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return true;
  });

  const getRoleLabel = (role: string) => {
    return getLegacyRoleLabel(role as LegacyUserRole) || role;
  };

  const getStatusBadge = (u: any) => {
    if (u.deletedAt) {
      return <Badge variant="destructive" className="text-xs">Excluído</Badge>;
    }
    if (!u.isActive) {
      return <Badge variant="secondary" className="text-xs bg-orange-100 text-orange-700 border-orange-200">Inativo</Badge>;
    }
    return <Badge variant="secondary" className="text-xs bg-green-100 text-green-700 border-green-200">Ativo</Badge>;
  };

  const getLifecycleActionLabel = (action: LifecycleAction | null) => {
    if (action === "deactivate") return { title: "Inativar Usuário", verb: "inativar", btnLabel: "Inativar", btnClass: "bg-orange-500 hover:bg-orange-600 text-white" };
    if (action === "activate") return { title: "Reativar Usuário", verb: "reativar", btnLabel: "Reativar", btnClass: "bg-green-600 hover:bg-green-700 text-white" };
    if (action === "delete") return { title: "Excluir Usuário", verb: "excluir permanentemente", btnLabel: "Excluir", btnClass: "bg-red-600 hover:bg-red-700 text-white" };
    return { title: "", verb: "", btnLabel: "", btnClass: "" };
  };

  // Contagens por status para as abas
  const counts = {
    all: (users || []).length,
    active: (users || []).filter(u => u.isActive && !u.deletedAt).length,
    inactive: (users || []).filter(u => !u.isActive && !u.deletedAt).length,
    deleted: (users || []).filter(u => !!u.deletedAt).length,
  };

  const lifecycleLabels = getLifecycleActionLabel(confirmLifecycle.action);

  return (
    <SalviaLayout title="Gestao de Usuarios">
      {/* Breadcrumbs */}
      <div className="mb-6 flex items-center gap-2 text-sm text-muted-foreground">
        <button onClick={() => setLocation("/portal")} className="hover:text-foreground transition-colors">
          Portal PJC
        </button>
        <span>/</span>
        <button onClick={() => setLocation(user?.role === "admin" || user?.role === "salvia_operation_admin" ? "/modulos/nucleo" : user?.role === "client_rh" ? "/minha-empresa" : "/clients")} className="hover:text-foreground transition-colors">
          {managementContextLabel}
        </button>
        <span>/</span>
        <span className="text-foreground font-medium">Usuários</span>
      </div>

      <div className="space-y-6">
        <div className="space-y-2">
          <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-slate-600">
            {managementContextLabel}
          </div>
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-1">
              <h1 className="text-3xl font-bold text-foreground">Gestão de Usuários</h1>
              <p className="text-sm text-muted-foreground">
                {managementDescription}
              </p>
            </div>
            {canCreateOrEditUsers ? (
              <Button
                onClick={() => handleOpenDialog()}
                className="bg-[#C0FF00] text-black hover:bg-[#b0ef00]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Criar Usuário
              </Button>
            ) : null}
          </div>
        </div>

        {/* Filtro de busca */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nome ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </div>

        {/* Abas de status */}
        <Tabs value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
          <TabsList>
            <TabsTrigger value="active">
              Ativos <Badge variant="secondary" className="ml-2 text-xs">{counts.active}</Badge>
            </TabsTrigger>
            <TabsTrigger value="inactive">
              Inativos <Badge variant="secondary" className="ml-2 text-xs">{counts.inactive}</Badge>
            </TabsTrigger>
            {user?.role === "admin" && (
              <TabsTrigger value="deleted">
                Excluídos <Badge variant="secondary" className="ml-2 text-xs">{counts.deleted}</Badge>
              </TabsTrigger>
            )}
            <TabsTrigger value="all">
              Todos <Badge variant="secondary" className="ml-2 text-xs">{counts.all}</Badge>
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#C0FF00]" />
          </div>
        ) : (
          <div className="space-y-3">
            {filteredUsers.length === 0 ? (
              <Card className="p-8 text-center">
                <UsersIcon className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Nenhum usuário encontrado</p>
              </Card>
            ) : (
              filteredUsers.map((u) => {
                const isDeleted = !!u.deletedAt;
                const isActive = u.isActive && !isDeleted;

                return (
                  <Card key={u.id} className={`p-4 hover:shadow-md transition-shadow ${isDeleted ? "opacity-60" : ""}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-foreground">{u.name}</h3>
                          {getStatusBadge(u)}
                        </div>
                        <p className="text-sm text-muted-foreground">{u.email}</p>
                        {u.phone && (
                          <p className="text-sm text-muted-foreground">
                            Telefone: <span className="font-medium">{u.phone}</span>
                          </p>
                        )}
                        <p className="text-sm text-muted-foreground">
                          Perfil: <span className="font-medium">{getRoleLabel(u.role)}</span>
                        </p>
                        {u.role === 'channel_admin_multi' && (u as any).channelIds?.length > 0 ? (
                          <p className="text-sm text-muted-foreground">
                            Canais: <span className="font-medium">
                              {((u as any).channelIds as number[]).map(cid => 
                                channels?.find(c => c.id === cid)?.name || `ID: ${cid}`
                              ).join(', ')}
                            </span>
                          </p>
                        ) : u.channelId ? (
                          <p className="text-sm text-muted-foreground">
                            Canal: <span className="font-medium">{channels?.find(c => c.id === u.channelId)?.name || `ID: ${u.channelId}`}</span>
                          </p>
                        ) : null}
                        {u.companyId && (
                          <p className="text-sm text-muted-foreground">
                            Empresa: <span className="font-medium">{companyNameById.get(u.companyId) || assignableCompanies.find((c: any) => c.id === u.companyId)?.nomeFantasia || assignableCompanies.find((c: any) => c.id === u.companyId)?.name || `ID: ${u.companyId}`}</span>
                          </p>
                        )}
                        {u.deactivatedAt && !isDeleted && (
                          <p className="text-xs text-orange-600 mt-1">
                            Inativado em: {new Date(u.deactivatedAt).toLocaleDateString("pt-BR")}
                          </p>
                        )}
                        {isDeleted && u.deletedAt && (
                          <p className="text-xs text-red-600 mt-1">
                            Excluído em: {new Date(u.deletedAt).toLocaleDateString("pt-BR")}
                          </p>
                        )}
                      </div>

                      {/* Menu de ações — não exibir para usuários excluídos */}
                      {!isDeleted && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {/* Editar — exclusivo do Admin Master Sálvia */}
                            {canCreateOrEditUsers ? (
                              <DropdownMenuItem onClick={() => handleOpenDialog(u)}>
                                <Edit className="w-4 h-4 mr-2" />
                                Editar
                              </DropdownMenuItem>
                            ) : null}

                            {/* Convite/Reset — gestão delegada por escopo */}
                            {canManageUsersScope && (
                              <>
                                <DropdownMenuSeparator />
                                {!u.passwordHash && (
                                  <DropdownMenuItem
                                    onClick={() => sendInvite.mutate({ userId: u.id })}
                                    disabled={sendInvite.isPending}
                                  >
                                    {sendInvite.isPending ? (
                                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                      <Mail className="w-4 h-4 mr-2" />
                                    )}
                                    Enviar Convite
                                  </DropdownMenuItem>
                                )}
                                {canResetPasswords && u.passwordHash && (
                                  <DropdownMenuItem
                                    onClick={() => setConfirmResetDialog({ open: true, userId: u.id, userEmail: u.email ?? "" })}
                                  >
                                    <KeyRound className="w-4 h-4 mr-2" />
                                    Resetar Senha
                                  </DropdownMenuItem>
                                )}
                              </>
                            )}

                            {/* Ações de ciclo de vida */}
                            {canManageUsersScope && u.id !== user?.id && (
                              <>
                                <DropdownMenuSeparator />
                                {isActive ? (
                                  <DropdownMenuItem
                                    onClick={() => openLifecycleDialog("deactivate", u)}
                                    className="text-orange-600 focus:text-orange-600"
                                  >
                                    <UserX className="w-4 h-4 mr-2" />
                                    Inativar
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem
                                    onClick={() => openLifecycleDialog("activate", u)}
                                    className="text-green-600 focus:text-green-600"
                                  >
                                    <UserCheck className="w-4 h-4 mr-2" />
                                    Reativar
                                  </DropdownMenuItem>
                                )}

                                {/* Excluir — apenas Admin Sálvia */}
                                {canDeleteUsersGlobally && (
                                  <DropdownMenuItem
                                    onClick={() => openLifecycleDialog("delete", u)}
                                    className="text-red-600 focus:text-red-600"
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Excluir
                                  </DropdownMenuItem>
                                )}
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </Card>
                );
              })
            )}
          </div>
        )}

        {/* Dialog de Editar/Criar Usuário */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{isCreating ? "Criar Usuário" : "Editar Usuário"}</DialogTitle>
            </DialogHeader>

            {editingUser && (
              <div className="space-y-4">
                {validationErrors.length > 0 && (
                  <Alert variant="destructive">
                    <AlertDescription>
                      <ul className="list-disc list-inside">
                        {validationErrors.map((error, idx) => (
                          <li key={idx}>{error}</li>
                        ))}
                      </ul>
                    </AlertDescription>
                  </Alert>
                )}

                <div className="border-b pb-4">
                  <h3 className="font-semibold mb-3">Informações Básicas</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium">Nome *</label>
                      <Input
                        value={editingUser.name}
                        onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                        placeholder="Nome completo"
                        className="mt-1"
                      />
                    </div>
                    {isCreating && (
                      <div>
                        <label className="text-sm font-medium">Email *</label>
                        <Input
                          value={editingUser.email}
                          onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                          placeholder="email@example.com"
                          type="email"
                          className="mt-1"
                        />
                      </div>
                    )}
                    {!isCreating && (
                      <div>
                        <label className="text-sm font-medium">Email</label>
                        <Input value={editingUser.email} disabled className="mt-1" />
                        <p className="text-xs text-muted-foreground mt-1">Somente leitura</p>
                      </div>
                    )}
                    <div>
                      <label className="text-sm font-medium">Telefone</label>
                      <Input
                        value={editingUser.phone}
                        onChange={(e) => setEditingUser({ ...editingUser, phone: e.target.value })}
                        placeholder="(11) 99999-9999"
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>

                <div className="border-b pb-4">
                  <div className="flex flex-col gap-1">
                    <h3 className="font-semibold">Perfil e Permissões</h3>
                    <p className="text-sm text-muted-foreground">
                      O Papel estrutural define apenas o Nível 1. A classificação operacional abaixo mantém a compatibilidade com os acessos já existentes no legado.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium">Papel estrutural *</label>
                      <Select
                        value={structuralRole}
                        onValueChange={(value) => handleStructuralRoleChange(value as StructuralRole)}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Selecione o papel estrutural..." />
                        </SelectTrigger>
                        <SelectContent>
                          {STRUCTURAL_ROLE_OPTIONS.map((role) => (
                            <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="mt-2 text-xs text-muted-foreground">
                        {STRUCTURAL_ROLE_OPTIONS.find((role) => role.value === structuralRole)?.description}
                      </p>
                    </div>

                    {isChannelStructuralRole && (
                      <div>
                        <label className="text-sm font-medium">Abrangência do vínculo *</label>
                        <Select
                          value={channelScopeMode}
                          onValueChange={(value) => handleChannelScopeModeChange(value as ChannelScopeMode)}
                        >
                          <SelectTrigger className="mt-2">
                            <SelectValue placeholder="Selecione a abrangência..." />
                          </SelectTrigger>
                          <SelectContent>
                            {CHANNEL_SCOPE_OPTIONS.map((scope) => (
                              <SelectItem key={scope.value} value={scope.value}>{scope.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="mt-2 text-xs text-muted-foreground">
                          {CHANNEL_SCOPE_OPTIONS.find((scope) => scope.value === channelScopeMode)?.description}
                        </p>
                      </div>
                    )}

                    <div>
                      <label className="text-sm font-medium">Classificação operacional transitória *</label>
                      <Select
                        value={operationalProfile}
                        onValueChange={(value) => handleOperationalProfileChange(value as OperationalProfile)}
                        disabled={operationalProfileOptions.length === 1}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Selecione a classificação operacional..." />
                        </SelectTrigger>
                        <SelectContent>
                          {operationalProfileOptions.map((profile) => (
                            <SelectItem key={profile.value} value={profile.value}>{profile.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="mt-2 text-xs text-muted-foreground">
                        {operationalProfileOptions.find((profile) => profile.value === operationalProfile)?.description}
                      </p>
                    </div>

                    {isMultiChannelScope && (
                      <Alert>
                        <AlertDescription>
                          No modelo legado atual, a abrangência multicanal permanece disponível apenas para a classificação Gestor do Canal.
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>
                </div>

                {isMultiChannelScope && (
                  <div className="border-b pb-4">
                    <h3 className="font-semibold mb-3">Canais Vinculados *</h3>
                    <p className="text-xs text-muted-foreground mb-3">Selecione os canais que este gestor poderá administrar. Ele poderá alternar entre eles após o login.</p>
                    <div className="space-y-2 max-h-48 overflow-y-auto border rounded-md p-3">
                      {channels?.map((channel) => {
                        const isChecked = selectedChannelIds.includes(channel.id);
                        return (
                          <label key={channel.id} className="flex items-center gap-2 cursor-pointer hover:bg-accent/50 rounded px-2 py-1.5">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => {
                                if (isChecked) {
                                  setSelectedChannelIds(selectedChannelIds.filter(id => id !== channel.id));
                                } else {
                                  setSelectedChannelIds([...selectedChannelIds, channel.id]);
                                }
                              }}
                              className="rounded border-gray-300"
                            />
                            <span className="text-sm">{channel.name}</span>
                          </label>
                        );
                      })}
                      {(!channels || channels.length === 0) && (
                        <p className="text-sm text-muted-foreground">Nenhum canal cadastrado</p>
                      )}
                    </div>
                    {selectedChannelIds.length > 0 && (
                      <p className="text-xs text-muted-foreground mt-2">{selectedChannelIds.length} canal(is) selecionado(s)</p>
                    )}
                  </div>
                )}

                {isSingleChannelScope && (
                  <div className="border-b pb-4">
                    <h3 className="font-semibold mb-3">Vinculações</h3>
                    <div>
                      <label className="text-sm font-medium">Vincular a Canal *</label>
                      <Select
                        value={editingUser.channelId}
                        onValueChange={(value) => {
                          setSelectedUserServiceIds([]);
                          setSelectedFunctionalProfiles({});
                          setFunctionalAssignmentNotesByService({});
                          setEditingUser({ ...editingUser, channelId: value, companyId: "" });
                        }}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Selecione um canal..." />
                        </SelectTrigger>
                        <SelectContent>
                          {channels?.map((channel) => (
                            <SelectItem key={channel.id} value={channel.id.toString()}>{channel.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {isClientStructuralRole && (
                  <div className="border-b pb-4">
                    <h3 className="font-semibold mb-3">Vinculações</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium">Vincular a Canal *</label>
                        <Select
                          value={editingUser.channelId}
                          onValueChange={(value) => {
                            resetOperationalAccessSelections();
                            setEditingUser({ ...editingUser, channelId: value, companyId: "" });
                          }}
                        >
                          <SelectTrigger className="mt-2">
                            <SelectValue placeholder="Selecione um canal..." />
                          </SelectTrigger>
                          <SelectContent>
                            {channels?.map((channel) => (
                              <SelectItem key={channel.id} value={channel.id.toString()}>{channel.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-sm font-medium">Vincular a Empresa *</label>
                        <Select
                          value={editingUser.companyId}
                          onValueChange={(value) => {
                            resetOperationalAccessSelections();
                            setEditingUser({ ...editingUser, companyId: value });
                          }}
                          disabled={!editingUser.channelId}
                        >
                          <SelectTrigger className="mt-2">
                            <SelectValue placeholder={editingUser.channelId ? "Selecione uma empresa..." : "Selecione um canal primeiro..."} />
                          </SelectTrigger>
                          <SelectContent>
                            {assignableCompanies.map((company: any) => (
                              <SelectItem key={company.id} value={company.id.toString()}>{company.nomeFantasia || company.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}

                {canManageUsersScope && (
                  <div className="border-b pb-4 space-y-4">
                    <div className="flex flex-col gap-1">
                      <h3 className="font-semibold">Permissões Operacionais por Serviço</h3>
                      <p className="text-sm text-muted-foreground">
                        {isCreating
                          ? "Defina já no cadastro inicial quais serviços operacionais serão liberados. O convite só será enviado quando a jornada estiver completa."
                          : "Ajuste somente serviços já elegíveis para este usuário conforme a cadeia canal habilitado → empresa contratante → usuário autorizado."}
                      </p>
                    </div>

                    {isCreating && !isCreationPreviewReady ? (
                      <Alert>
                        <AlertDescription>
                          Defina o papel estrutural, a abrangência aplicável e os vínculos obrigatórios antes de liberar as permissões operacionais por serviço.
                        </AlertDescription>
                      </Alert>
                    ) : isCreating && creationAccessPreviewError ? (
                      <Alert variant="destructive">
                        <AlertDescription>
                          {creationAccessPreviewError.message || "Não foi possível resolver as elegibilidades do cadastro inicial neste momento."}
                        </AlertDescription>
                      </Alert>
                    ) : !isCreating && editAccessPreviewError ? (
                      <Alert variant="destructive">
                        <AlertDescription>
                          {editAccessPreviewError.message || "Não foi possível recalcular as elegibilidades com os vínculos em edição neste momento."}
                        </AlertDescription>
                      </Alert>
                    ) : (isCreating ? isCreationPreviewResolving : (isLoadingUserServicePermissions || isEditPreviewResolving)) ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {isCreating ? "Resolvendo elegibilidades do cadastro inicial..." : "Carregando permissões por serviço..."}
                      </div>
                    ) : servicePermissionCatalog.length === 0 ? (
                      <Alert>
                        <AlertDescription>
                          {isCreating
                            ? "Nenhum serviço foi encontrado para a combinação atual de papel estrutural, abrangência, canal e empresa. Revise os vínculos antes de concluir o cadastro."
                            : "Este usuário ainda não possui serviços elegíveis para configuração operacional."}
                        </AlertDescription>
                      </Alert>
                    ) : (
                      <div className="space-y-3">
                        {hasChannelEnabledServicesWithoutEligibility && !isChannelStructuralRole && (
                          <Alert>
                            <AlertDescription>
                              O canal já possui serviços habilitados comercialmente, mas ainda não existem contratos ativos vinculados a esse canal para liberar a autorização operacional deste usuário. Revise os contratos ativos antes de concluir o cadastro.
                            </AlertDescription>
                          </Alert>
                        )}
                        {!isCreating && hasPendingUserDraftChanges && (
                          <Alert>
                            <AlertDescription>
                              O catálogo abaixo já reflete o canal, a empresa e o papel estrutural em edição. Ao salvar permissões ou perfil funcional, o cadastro do usuário será persistido primeiro para evitar divergência entre contrato ativo e elegibilidade operacional.
                            </AlertDescription>
                          </Alert>
                        )}
                        {servicePermissionCatalog.map((service: any) => {
                          const checked = selectedUserServiceIds.includes(service.id);
                          const disabled = !service.eligibleForUser;
                          return (
                            <label
                              key={service.id}
                              className={`flex items-start justify-between gap-4 rounded-lg border p-3 ${disabled ? "bg-muted/40 opacity-70" : "hover:bg-accent/40 cursor-pointer"}`}
                            >
                              <div className="space-y-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="font-medium text-sm">{service.name}</span>
                                  <Badge variant={service.channelEnabled ? "secondary" : "outline"}>
                                    {service.channelEnabled ? "Canal habilitado" : "Canal não habilitado"}
                                  </Badge>
                                  {isChannelStructuralRole ? (
                                    <Badge variant={service.channelEnabled ? "secondary" : "outline"}>
                                      {service.channelEnabled ? "Elegível pelo canal" : "Canal sem vínculo ativo"}
                                    </Badge>
                                  ) : (
                                    <Badge variant={service.companyContracted ? "secondary" : "outline"}>
                                      {service.companyContracted ? "Contrato ativo" : "Sem contrato"}
                                    </Badge>
                                  )}
                                  {checked && <Badge className="bg-[#C0FF00] text-black hover:bg-[#C0FF00]">Autorizado</Badge>}
                                </div>
                                {service.description && (
                                  <p className="text-xs text-muted-foreground">{service.description}</p>
                                )}
                                {!service.eligibleForUser && (
                                  <p className="text-xs text-amber-700">
                                    {isChannelStructuralRole
                                      ? "Este serviço não pode ser atribuído porque o canal ainda não possui vínculo ativo com esse serviço."
                                      : "Este serviço não pode ser atribuído porque ainda não está elegível na cadeia comercial/contratual deste usuário."}
                                  </p>
                                )}
                              </div>
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={disabled || (!isCreating && saveUserServicePermissions.isPending)}
                                onChange={(e) => handleToggleUserServicePermission(service.id, e.target.checked)}
                                className="mt-1 h-4 w-4 rounded border-gray-300"
                              />
                            </label>
                          );
                        })}

                        {!isCreating && (
                          <div className="flex justify-end">
                            <Button
                              type="button"
                              onClick={handleSaveUserServicePermissions}
                              disabled={saveUserServicePermissions.isPending}
                              className="bg-[#002F59] hover:bg-[#001a33] text-white"
                            >
                              {saveUserServicePermissions.isPending || (keepDialogOpenAfterUserSave && updateUser.isPending) ? (
                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Salvando permissões...</>
                              ) : hasPendingUserDraftChanges ? (
                                "Salvar usuário + permissões por serviço"
                              ) : (
                                "Salvar permissões por serviço"
                              )}
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {canManageUsersScope && (
                  <div className="border-b pb-4 space-y-4">
                    <div className="flex flex-col gap-1">
                      <h3 className="font-semibold">Perfil Funcional do Nível 2</h3>
                      <p className="text-sm text-muted-foreground">
                        {isCreating
                          ? "Defina o perfil funcional já no cadastro inicial sempre que houver catálogo aplicável. Isso evita edição posterior apenas para liberar operação final."
                          : "Depois da elegibilidade estrutural e da autorização por serviço, defina o perfil funcional ativo que governa a operação final do NR1."}
                      </p>
                    </div>

                    {isCreating && !isCreationPreviewReady ? (
                      <Alert>
                        <AlertDescription>
                          O Perfil Funcional do Nível 2 será destravado assim que o papel estrutural e os vínculos mínimos estiverem consistentes.
                        </AlertDescription>
                      </Alert>
                    ) : isCreating && creationAccessPreviewError ? (
                      <Alert variant="destructive">
                        <AlertDescription>
                          {creationAccessPreviewError.message || "Não foi possível resolver o catálogo funcional aplicável neste momento."}
                        </AlertDescription>
                      </Alert>
                    ) : (isCreating ? isCreationPreviewResolving : isLoadingUserFunctionalAssignment) ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {isCreating ? "Resolvendo catálogo funcional aplicável..." : "Carregando perfis funcionais disponíveis..."}
                      </div>
                    ) : functionalProfileCatalogNr1.length === 0 ? (
                      <Alert>
                        <AlertDescription>
                          {isCreating
                            ? "Nenhum perfil funcional do Nível 2 é exigido para o conjunto atual de vínculos e serviços selecionados."
                            : "Não foi possível resolver o catálogo funcional do Nível 2 para este usuário."}
                        </AlertDescription>
                      </Alert>
                    ) : (
                      <div className="space-y-4 rounded-lg border p-4">
                        <div className="flex flex-wrap gap-2">
                          <Badge variant="secondary">
                            Nível 1: {isCreating ? effectiveCreationAccessPreview?.level1 ?? "Em resolução" : userFunctionalAssignment?.level1 ?? "—"}
                          </Badge>
                          {!isCreating && userFunctionalAssignment?.activeProfile?.profileName && (
                            <Badge className="bg-[#C0FF00] text-black hover:bg-[#C0FF00]">
                              Perfil atual: {userFunctionalAssignment.activeProfile.profileName}
                            </Badge>
                          )}
                        </div>

                        <div>
                          <label className="text-sm font-medium">Perfil funcional ativo no NR1</label>
                          <Select
                            value={selectedFunctionalProfiles.nr1 || undefined}
                            onValueChange={(value) => setSelectedFunctionalProfiles((current) => ({ ...current, nr1: value }))}
                          >
                            <SelectTrigger className="mt-2">
                              <SelectValue placeholder="Selecione o perfil funcional..." />
                            </SelectTrigger>
                            <SelectContent>
                              {functionalProfileCatalogNr1.map((profile: any) => (
                                <SelectItem key={profile.key} value={profile.key}>
                                  {profile.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <label className="text-sm font-medium">Observação da atribuição</label>
                          <Input
                            className="mt-2"
                            placeholder="Opcional: motivo operacional ou contexto da atribuição"
                            value={functionalAssignmentNotesByService.nr1 || ""}
                            onChange={(event) => setFunctionalAssignmentNotesByService((current) => ({ ...current, nr1: event.target.value }))}
                          />
                        </div>

                        {!isCreating && (userFunctionalAssignment?.activeProfile?.permissionKeys?.length ?? 0) > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {(userFunctionalAssignment?.activeProfile?.permissionKeys ?? []).map((permissionKey: string) => (
                              <Badge key={permissionKey} variant="outline">{permissionKey}</Badge>
                            ))}
                          </div>
                        )}

                        <div className="flex items-center justify-between gap-3">
                          <p className="text-xs text-muted-foreground">
                            Se precisar criar ou refinar perfis, use a tela dedicada em Configurações → Perfis Funcionais.
                          </p>
                          {!isCreating && (
                            <Button
                              type="button"
                              onClick={handleSaveFunctionalProfile}
                              disabled={assignFunctionalProfile.isPending || !selectedFunctionalProfiles.nr1}
                              className="bg-[#002F59] hover:bg-[#001a33] text-white"
                            >
                              {assignFunctionalProfile.isPending ? (
                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Salvando perfil...</>
                              ) : (
                                "Salvar perfil funcional"
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Seção Status do Usuário — somente no modo edição, com RBAC */}
                {/* DEBUG: isCreating={isCreating}, originalUserData={originalUserData !== null}, role={user?.role}, editingId={editingUser.id}, userId={user?.id} */}
                {!isCreating && originalUserData && canManageUsersScope && (
                  <div className="border-b pb-4">
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <Power className="w-4 h-4" />
                      Status do Usuário
                    </h3>

                    {/* Usuário excluído: somente leitura */}
                    {originalUserData.deletedAt ? (
                      <div className="flex items-center gap-3 p-3 rounded-lg bg-red-50 border border-red-200">
                        <Badge variant="destructive" className="text-xs">Excluído</Badge>
                        <span className="text-sm text-red-700">Este usuário foi excluído e não pode ser reativado.</span>
                      </div>
                    ) : (
                      <>
                        {/* Toggle de status */}
                        <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border">
                          <div className="flex items-center gap-3">
                            <Switch
                              checked={originalUserData.isActive}
                              disabled={isLifecyclePending || editingUser.id === user?.id}
                              onCheckedChange={(checked) => {
                                if (editingUser.id === user?.id) return; // Proteção extra
                                if (checked) {
                                  // Reativar — ação direta (sem modal de confirmação)
                                  activateUser.mutate({ userId: editingUser.id, reason: undefined });
                                } else {
                                  // Desativar — abrir confirmação inline
                                  setInlineStatusConfirm({ open: true, action: "deactivate", reason: "" });
                                }
                              }}
                            />
                            <div>
                              <span className="text-sm font-medium">
                                {originalUserData.isActive ? "Usuário ativo" : "Usuário inativo"}
                              </span>
                              {originalUserData.isActive ? (
                                <p className="text-xs text-muted-foreground">O usuário pode acessar o sistema normalmente.</p>
                              ) : (
                                <p className="text-xs text-orange-600">
                                  O usuário está impedido de acessar o sistema.
                                  {originalUserData.deactivatedAt && (
                                    <> Inativado em: {new Date(originalUserData.deactivatedAt).toLocaleDateString("pt-BR")}</>
                                  )}
                                </p>
                              )}
                            </div>
                          </div>
                          <div>
                            {originalUserData.isActive
                              ? <Badge variant="secondary" className="text-xs bg-green-100 text-green-700 border-green-200">Ativo</Badge>
                              : <Badge variant="secondary" className="text-xs bg-orange-100 text-orange-700 border-orange-200">Inativo</Badge>
                            }
                          </div>
                        </div>
                        {editingUser.id === user?.id && (
                          <p className="text-xs text-muted-foreground mt-2 italic">Você não pode alterar seu próprio status.</p>
                        )}

                        {/* Loading indicator */}
                        {isLifecyclePending && (
                          <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Processando alteração de status...
                          </div>
                        )}

                        {/* Confirmação inline para desativar */}
                        {inlineStatusConfirm.open && inlineStatusConfirm.action === "deactivate" && (
                          <div className="mt-3 p-3 rounded-lg border border-orange-200 bg-orange-50 space-y-3">
                            <p className="text-sm text-orange-800 font-medium">
                              Ao desativar, o usuário não conseguirá mais acessar o sistema até ser reativado.
                            </p>
                            <div>
                              <label className="text-xs font-medium text-muted-foreground">Motivo (opcional)</label>
                              <Input
                                placeholder="Informe o motivo da inativação..."
                                value={inlineStatusConfirm.reason}
                                onChange={(e) => setInlineStatusConfirm({ ...inlineStatusConfirm, reason: e.target.value })}
                                className="mt-1 text-sm"
                              />
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setInlineStatusConfirm({ open: false, action: null, reason: "" })}
                                disabled={deactivateUser.isPending}
                              >
                                Cancelar
                              </Button>
                              <Button
                                size="sm"
                                className="bg-orange-500 hover:bg-orange-600 text-white"
                                disabled={deactivateUser.isPending}
                                onClick={() => {
                                  deactivateUser.mutate({
                                    userId: editingUser.id,
                                    reason: inlineStatusConfirm.reason || undefined,
                                  });
                                }}
                              >
                                {deactivateUser.isPending ? (
                                  <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Inativando...</>
                                ) : (
                                  "Confirmar Inativação"
                                )}
                              </Button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Seção Danger Zone — Excluir Usuário (somente edição, RBAC admin, não auto-ação) */}
                {!isCreating && originalUserData && !originalUserData.deletedAt && canDeleteUsersGlobally && editingUser.id !== user?.id && (
                  <div className="border border-red-300 rounded-lg p-4 bg-red-50/50">
                    <h3 className="font-semibold mb-2 text-red-700 flex items-center gap-2">
                      <Trash2 className="w-4 h-4" />
                      Zona de Perigo
                    </h3>
                    <p className="text-sm text-red-600 mb-3">
                      A exclusão é permanente. O usuário perderá todo o acesso ao sistema e não poderá ser reativado.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-red-300 text-red-700 hover:bg-red-100 hover:text-red-800"
                      onClick={() => {
                        // Abrir o dialog de confirmação de exclusão (reusa o lifecycle dialog)
                        openLifecycleDialog("delete", {
                          id: editingUser.id,
                          name: editingUser.name,
                          email: editingUser.email,
                        });
                      }}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Excluir Usuário Permanentemente
                    </Button>
                  </div>
                )}

                <div className="flex gap-3 pt-4 border-t">
                  <Button variant="outline" onClick={() => { setIsDialogOpen(false); resetForm(); }}>
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleSave}
                    className="bg-[#C0FF00] text-black hover:bg-[#b0ef00]"
                    disabled={updateUser.isPending || createCompleteUser.isPending}
                  >
                    {updateUser.isPending || createCompleteUser.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      "Salvar"
                    )}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Dialog de Confirmação de Reset de Senha */}
        <Dialog open={confirmResetDialog.open} onOpenChange={(open) => setConfirmResetDialog({ ...confirmResetDialog, open })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Resetar Senha</DialogTitle>
              <DialogDescription>
                Deseja enviar um email de redefinição de senha para <strong>{confirmResetDialog.userEmail}</strong>?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmResetDialog({ open: false, userId: null, userEmail: "" })}>
                Cancelar
              </Button>
              <Button
                onClick={() => { if (confirmResetDialog.userId) resetPassword.mutate({ userId: confirmResetDialog.userId }); }}
                disabled={resetPassword.isPending}
                className="bg-[#002F59] hover:bg-[#001a33] text-white"
              >
                {resetPassword.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enviando...</> : "Enviar Email"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog de Confirmação de Ciclo de Vida */}
        <Dialog open={confirmLifecycle.open} onOpenChange={(open) => !open && closeLifecycleDialog()}>
          <DialogContent>
            <DialogHeader>
              <div className="flex items-center gap-2">
                <ShieldAlert className={`w-5 h-5 ${confirmLifecycle.action === "delete" ? "text-red-500" : confirmLifecycle.action === "deactivate" ? "text-orange-500" : "text-green-500"}`} />
                <DialogTitle>{lifecycleLabels.title}</DialogTitle>
              </div>
              <DialogDescription>
                {confirmLifecycle.action === "delete" ? (
                  <>
                    Você está prestes a <strong>excluir permanentemente</strong> o usuário{" "}
                    <strong>{confirmLifecycle.userName}</strong> ({confirmLifecycle.userEmail}).
                    <br /><br />
                    <span className="text-red-600 font-medium">Esta ação não pode ser desfeita.</span> O usuário não poderá mais fazer login.
                  </>
                ) : confirmLifecycle.action === "deactivate" ? (
                  <>
                    Você está prestes a <strong>inativar</strong> o usuário{" "}
                    <strong>{confirmLifecycle.userName}</strong> ({confirmLifecycle.userEmail}).
                    <br /><br />
                    O usuário não poderá mais fazer login até ser reativado.
                  </>
                ) : (
                  <>
                    Você está prestes a <strong>reativar</strong> o usuário{" "}
                    <strong>{confirmLifecycle.userName}</strong> ({confirmLifecycle.userEmail}).
                    <br /><br />
                    O usuário poderá fazer login novamente.
                  </>
                )}
              </DialogDescription>
            </DialogHeader>

            {/* Campo de motivo (opcional) */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                Motivo <span className="text-xs">(opcional)</span>
              </label>
              <Input
                placeholder="Informe o motivo desta ação..."
                value={confirmLifecycle.reason}
                onChange={(e) => setConfirmLifecycle({ ...confirmLifecycle, reason: e.target.value })}
              />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={closeLifecycleDialog} disabled={isLifecyclePending}>
                Cancelar
              </Button>
              <Button
                onClick={handleConfirmLifecycle}
                disabled={isLifecyclePending}
                className={lifecycleLabels.btnClass}
              >
                {isLifecyclePending ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processando...</>
                ) : (
                  lifecycleLabels.btnLabel
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </SalviaLayout>
  );
}
